// src/components/lists/base/ListStyles.tsx
import React from 'react';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { colors } from '../../../theme/theme';
import { useDragScroll } from '../../../hooks/useDragScroll';

// Animations
export const slideInFromRight = keyframes`
  from {
    opacity: 0.3;
    transform: translateX(150px);
  }
  to {
    opacity: 1;
    transform: translateX(-50px);
  }
`;

// Main container
export const ListContainer = styled(Box)({
  width: '100%',
  backgroundColor: 'transparent',
});

// List row - Alternating row colors, open/closed states
export const ListRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isEven',
})<{ isOpen?: boolean; isEven?: boolean }>(({ theme, isOpen, isEven }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  backgroundColor: isOpen
    ? theme.palette.mode === 'dark'
      ? colors.grey[300]
      : theme.palette.primary.dark
    : isEven
      ? theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : colors.grey[50]
      : theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.grey[300],
  borderBottom: `1px solid ${theme.palette.primary.dark}`,
  overflow: 'hidden',
  minHeight: '5rem',
  height: 'auto',
  '&:hover': {
    backgroundColor: isOpen
      ? theme.palette.mode === 'dark'
        ? colors.grey[300]
        : theme.palette.primary.dark
      : theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.tertiary.light,
  },
  '&:focus-within': {
    outline: `3px solid ${colors.accessibility.focusColor}`,
    outlineOffset: '-3px',
  },
}));

// Clickable area for row interaction
export const RowClickableArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '80px',
  zIndex: isOpen ? 1 : 10,
  cursor: isOpen ? 'default' : 'pointer',
  display: isOpen ? 'none' : 'block',
}));

// Item name/header section container
const ItemNameSectionRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'relative',
  left: '0',
  transform: isOpen ? 'translateY(0.9rem)' : 'translateY(20%)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  zIndex: 20,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  padding: '0.5rem 0',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  paddingRight: '20px',
  '&::-webkit-scrollbar': { display: 'none' },
  '@media (min-width: 600px)': {
    left: '1.25rem',
    transform: isOpen ? 'translateY(1rem)' : 'translateY(18%)',
  },
}));

// Mouse drag-to-pan added here so desktop users can reach overflowing
// content the same way touch users already can via native scrolling.
export const ItemNameSection: React.FC<
  React.ComponentProps<typeof ItemNameSectionRoot>
> = (props) => {
  const dragRef = useDragScroll<HTMLDivElement>();
  return <ItemNameSectionRoot ref={dragRef} {...props} />;
};

// Icon container
export const ItemIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  marginRight: '0.25rem',
  marginLeft: isOpen ? '0.3rem' : '0.25rem',
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: isOpen ? '2.2rem' : '2.5rem',
    color: isOpen
      ? theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText
      : theme.palette.mode === 'dark'
        ? colors.grey[0]
        : theme.palette.grey[900],
  },
  '@media (min-width: 600px)': {
    marginRight: '1rem',
    marginLeft: 0,
    '& .MuiSvgIcon-root': { fontSize: '2.75rem' },
  },
}));

// Warning Icon container
export const ItemWarningIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  marginRight: '0.25rem',
  marginLeft: isOpen ? '0.2rem' : '0.25rem',
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: isOpen ? '2.2rem' : '2.5rem',
    color: isOpen
      ? theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText
      : theme.palette.mode === 'dark'
        ? colors.grey[0]
        : theme.palette.grey[900],
  },
  '@media (min-width: 600px)': {
    marginRight: '0.25rem',
    marginLeft: 0,
    '& .MuiSvgIcon-root': { fontSize: '2.75rem' },
  },
}));

// Still Owned Icon container
export const StillOwnedIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  marginRight: 0,
  marginLeft: 0,
  display: 'flex',
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: isOpen ? '2.2rem' : '2.5rem',
    color: isOpen
      ? theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText
      : theme.palette.mode === 'dark'
        ? colors.grey[0]
        : theme.palette.grey[900],
  },
  '@media (min-width: 600px)': {
    marginRight: 0,
    marginLeft: 0,
    '& .MuiSvgIcon-root': { fontSize: '2.75rem' },
  },
}));

// Item name typography
export const ItemName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ theme, isOpen }) => ({
  fontSize: '2.25rem',
  fontWeight: 400,
  lineHeight: '0.5rem',
  color: isOpen
    ? theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.contrastText
    : theme.palette.mode === 'dark'
      ? colors.grey[0]
      : theme.palette.grey[900],
  userSelect: 'none',
  '& .surname, .petname, .bookingSurname': { fontWeight: 700 },
  '& .bookingFirstname': {
    fontWeight: '500',
    color: isOpen
      ? theme.palette.mode === 'dark'
        ? theme.palette.grey[700]
        : theme.palette.primary.contrastText
      : theme.palette.mode === 'dark'
        ? theme.palette.primary.contrastText
        : colors.grey[700],
  },
  '& .bookingDate': { fontSize: '2rem', fontStyle: 'italic' },
  '& .bookingDateBold': { fontWeight: '700' },
  '@media (min-width: 600px)': { lineHeight: '1.5rem' },
}));

// Skeleton component for item name loading state
export const SkeletonItemName = styled(Skeleton, {
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'widthVariant',
})<{ isOpen?: boolean; widthVariant?: 'short' | 'medium' | 'long' }>(({
  widthVariant = 'medium',
}) => {
  const widthMap = { short: '120px', medium: '180px', long: '240px' };
  return {
    fontSize: '2.25rem',
    height: '2.25rem',
    width: widthMap[widthVariant],
    borderRadius: '4px',
    '@media (min-width: 600px)': { height: '2.5rem' },
  };
});

// Action buttons container
const ActionButtonsRoot = styled(Box)({
  position: 'relative',
  top: '1.75rem',
  right: 'unset',
  left: '55px',
  display: 'flex',
  flexDirection: 'row',
  gap: '2px',
  alignItems: 'center',
  justifyContent: 'flex-start',
  zIndex: 30,
  animation: `${slideInFromRight} 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.1s both`,
  overflowX: 'auto',
  overflowY: 'hidden',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  paddingRight: '20px',
  '&::-webkit-scrollbar': { display: 'none' },
  '@media (min-width: 600px)': { left: '75px' },
});

// Mouse drag-to-pan, same rationale as ItemNameSection above.
export const ActionButtons: React.FC<
  React.ComponentProps<typeof ActionButtonsRoot>
> = (props) => {
  const dragRef = useDragScroll<HTMLDivElement>();
  return <ActionButtonsRoot ref={dragRef} {...props} />;
};

// Expandable details section
export const DetailsSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen?: boolean }>(({ isOpen }) => ({
  position: 'relative',
  paddingLeft: '1.25rem',
  paddingTop: isOpen ? '0.5rem' : '0',
  paddingBottom: isOpen ? '1.25rem' : '0',
  maxHeight: isOpen ? '500px' : '0',
  height: 'auto',
  overflowY: 'clip',
  overflowX: 'visible',
  transition: 'max-height 0.3s ease-out, padding 0.3s ease-out',
  '@media (min-width: 600px)': { maxHeight: isOpen ? 'auto' : '0' },
}));

// Container for detail items
export const DetailSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  width: '100%',
  marginTop: '0.5rem',
  position: 'relative',
  '@media (min-width: 600px)': {
    flexDirection: 'row',
    marginTop: 0,
  },
});

// Individual detail item
const DetailItemRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  whiteSpace: 'nowrap',
  alignItems: 'center',
  gap: '8px',
  color:
    theme.palette.mode === 'dark'
      ? theme.palette.primary.dark
      : theme.palette.primary.contrastText,
  padding: '2px 32px 0 0',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  width: '100%',
  minWidth: 0,
  isolation: 'isolate',
  '&::-webkit-scrollbar': { display: 'none' },
  '& .MuiTypography-root': {
    fontSize: '2rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText,
  },
  '& .MuiSvgIcon-root': {
    display: 'inline-flex',
    fontSize: '2rem',
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.dark
        : theme.palette.primary.contrastText,
    flexShrink: 0,
  },
  '@media (min-width: 600px)': {
    gap: '12px',
    padding: '4px 0',
    '& .MuiTypography-root': { fontSize: '2.25rem' },
    '& .MuiSvgIcon-root': {
      display: 'inline-flex',
      fontSize: '2.25rem',
    },
  },
}));

// Mouse drag-to-pan, same rationale as ItemNameSection above.
export const DetailItem: React.FC<
  React.ComponentProps<typeof DetailItemRoot>
> = (props) => {
  const dragRef = useDragScroll<HTMLDivElement>();
  return <DetailItemRoot ref={dragRef} {...props} />;
};

// Copy button for detail items
export const CopyButton = styled(IconButton)(({ theme }) => ({
  padding: '6px',
  marginLeft: '8px',
  borderRadius: '50%',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(28, 46, 74, 0.2)'
        : theme.palette.primary.light + '40',
  },
  '&:focus': {
    outline: `3px solid ${colors.secondary.main}`,
    outlineOffset: '2px',
  },
  '@media (min-width: 600px)': { display: 'inline-flex' },
}));
